import React, { useState, useEffect, useCallback, useContext } from 'react';
import axiosInstance from './axiosInstance';
import { AuthContext } from './context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import ShortTermForecast from './ShortTermForecast';
import OverviewTab from './OverviewTab';
import ActivityLog from './ActivityLog';
import PSIForecast from './PSIForecast';
import ProtectedComponent from './ProtectedComponent';
import ModelPicker from './ModelPicker';
import WeeklySnapshotModal from './WeeklySnapshotModal';
import './styles/SalesRolloutForecasting.css';

// Activity Log tab is visible only to these admins (server enforces the same
// allowlist — this just hides the tab). James Jeong is included ahead of time.
const ACTIVITY_ADMINS = [
  'ericolsen@solum-group.com',
  'heeseokjung@solum-group.com',
  'jhs@solum-group.com',
  'mchoi@solum-group.com',
];

// DS PSI (digital-signage Purchase-Sales-Inventory) tab. Scoped to signage
// panels only — server-side filter in buildPSI() drops ESL/gateway SKUs.
const PSI_TAB_ENABLED = true;

// Current ISO calendar week + its Mon–Sun range, for the header "snapshot" badge.
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function isoWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}
function currentWeekLabel(date = new Date()) {
  const day = date.getDay();
  const monday = new Date(date);
  monday.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const year = sunday.getFullYear();
  const range = monday.getMonth() === sunday.getMonth()
    ? `${MONTHS_ABBR[monday.getMonth()]} ${monday.getDate()}–${sunday.getDate()}, ${year}`
    : `${MONTHS_ABBR[monday.getMonth()]} ${monday.getDate()} – ${MONTHS_ABBR[sunday.getMonth()]} ${sunday.getDate()}, ${year}`;
  return `Week ${isoWeekNumber(date)} · ${range}`;
}

/**
 * SalesRolloutForecasting - Company/Store Type/Products + Monthly Deliveries
 *
 * Click store type → Modal to edit product definitions
 * Main view → Monthly delivery snapshot (current + 5 months)
 */
function SalesRolloutForecasting() {
  const { user } = useContext(AuthContext);
  const isActivityAdmin = ACTIVITY_ADMINS.includes(String(user?.email || '').toLowerCase());

  // Tab: 'overview' = all-customers landing rollup, 'long' = 12-month rollout,
  // 'short' = 16-week weekly forecast
  const [activeTab, setActiveTab] = useState('overview');
  const [snapshotWeek, setSnapshotWeek] = useState(null); // { key, label } of the week being snapshotted

  // Open the weekly snapshot for a given week (Monday key from a column header).
  const openSnapshot = (weekKey) => {
    setSnapshotWeek({ key: weekKey, label: currentWeekLabel(new Date(weekKey + 'T12:00:00')) });
  };

  // STATE
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [storeTypes, setStoreTypes] = useState([]);

  // User filtering
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user ? String(user.id) : null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showAddStoreTypeModal, setShowAddStoreTypeModal] = useState(false);
  const [showEditStoreTypeModal, setShowEditStoreTypeModal] = useState(false);
  const [editingStoreType, setEditingStoreType] = useState(null);

  // Form state
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newStoreType, setNewStoreType] = useState({ name: '', description: '' });

  const [showProductManager, setShowProductManager] = useState(false);

  // SAI model catalog (shared by both tabs' pickers)
  const [models, setModels] = useState([]);
  const [showEditModelPicker, setShowEditModelPicker] = useState(false);

  // Catalog manager (gear modal)
  const [catalogModels, setCatalogModels] = useState([]); // includes inactive
  const [newModel, setNewModel] = useState({ name: '', category: '', blackCode: '', whiteCode: '', redCode: '' });

  // Summary modal
  const [showSummary, setShowSummary] = useState(null); // null=hidden, 'company'=single, 'all'=total
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Editing
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Drag and drop
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Reveal greyed-out prior months when user clicks "‹ prev"
  const [pastMonthsRevealed, setPastMonthsRevealed] = useState(false);
  const PAST_MONTHS_TO_REVEAL = 12;

  // Rolling 12 months: current month + 11 forward.
  // When pastMonthsRevealed, prepend up to PAST_MONTHS_TO_REVEAL prior months.
  const displayMonths = React.useMemo(() => {
    const months = [];
    const now = new Date();
    const startOffset = pastMonthsRevealed ? -PAST_MONTHS_TO_REVEAL : 0;
    const count = 12 + (pastMonthsRevealed ? PAST_MONTHS_TO_REVEAL : 0);
    for (let i = 0; i < count; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + startOffset + i, 1);
      months.push(d.toISOString().slice(0, 7));
    }
    return months;
  }, [pastMonthsRevealed]);

  const currentMonth = new Date().toISOString().slice(0, 7);

  // Months whose demand is also captured by the 16-week short-term window:
  // current month + next 3. Recomputes automatically on the 1st (page load).
  const coveredMonths = React.useMemo(() => {
    const now = new Date();
    const arr = [];
    for (let i = 0; i < 4; i++) {
      arr.push(new Date(now.getFullYear(), now.getMonth() + i, 1).toISOString().slice(0, 7));
    }
    return arr;
  }, []);

  const formatMonthShort = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  // DATA FETCHING
  // Companies list follows the user filter: a specific user only sees
  // companies they have a part in (their store types / short-term lines /
  // companies they created).
  const fetchCompanies = useCallback(async () => {
    try {
      const params = {};
      if (selectedUserId && selectedUserId !== 'all') params.userId = selectedUserId;
      const res = await axiosInstance.get('/forecast/companies', { params });
      setCompanies(res.data.companies || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
    }
  }, [selectedUserId]);

  const fetchStoreTypes = useCallback(async (company) => {
    if (!company) {
      setStoreTypes([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/forecast/store-types/${encodeURIComponent(company)}`);
      setStoreTypes(res.data.storeTypes || []);
    } catch (err) {
      console.error('Error fetching store types:', err);
      setError('Failed to load store types');
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchUsers = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/forecast/users');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/forecast/models');
      setModels(res.data.models || []);
    } catch (err) {
      console.error('Error fetching model catalog:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchModels();
  }, [fetchUsers, fetchModels]);

  // Refetch companies on mount and whenever the user filter changes
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // If the selected company fell out of the filtered list, clear it
  useEffect(() => {
    if (selectedCompany && companies.length > 0 && !companies.some(c => c.name === selectedCompany)) {
      setSelectedCompany(null);
      setStoreTypes([]);
    }
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchStoreTypes(selectedCompany);
    }
  }, [selectedCompany, fetchStoreTypes]);

  // Set to current user as soon as user data is available
  useEffect(() => {
    if (selectedUserId === null && user) {
      setSelectedUserId(String(user.id));
    }
  }, [user, selectedUserId]);

  // After store types load, fall back to "all" if user has none in this company
  useEffect(() => {
    if (user && storeTypes.length > 0 && selectedUserId === String(user.id)) {
      const userName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      const hasOwn = storeTypes.some(st =>
        String(st.createdBy) === String(user.id) ||
        (!st.createdBy && st.createdByName === userName)
      );
      if (!hasOwn) setSelectedUserId('all');
    }
  }, [user, storeTypes, selectedUserId]);

  // Filter store types by selected user (match by ID, fallback to name)
  const filteredStoreTypes = React.useMemo(() => {
    if (selectedUserId && selectedUserId !== 'all') {
      const selectedUser = users.find(u => u.id === selectedUserId);
      return storeTypes.filter(st =>
        String(st.createdBy) === selectedUserId ||
        (!st.createdBy && selectedUser && st.createdByName === selectedUser.name)
      );
    }
    return storeTypes;
  }, [storeTypes, selectedUserId, users]);

  // HANDLERS
  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      await axiosInstance.post('/forecast/companies', { name: newCompanyName });
      setNewCompanyName('');
      setShowAddCompanyModal(false);
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create company');
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    const company = companies.find(c => c.name === selectedCompany);
    if (!company) return;
    if (!window.confirm(`Delete company "${selectedCompany}" and all its store types? This cannot be undone.`)) return;
    try {
      await axiosInstance.delete(`/forecast/companies/${company._id}`);
      setSelectedCompany(null);
      setStoreTypes([]);
      setShowProductManager(false);
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete company');
    }
  };

  const handleAddStoreType = async () => {
    if (!newStoreType.name.trim()) return;
    try {
      await axiosInstance.post('/forecast/store-types', {
        company: selectedCompany,
        ...newStoreType
      });
      setNewStoreType({ name: '', description: '' });
      setShowAddStoreTypeModal(false);
      fetchStoreTypes(selectedCompany);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create store type';
      if (/already exists/i.test(msg)) {
        // The existing store type may be hidden by the current user filter
        // (e.g. it was created under a different account). Reveal it instead
        // of leaving the user stuck on a duplicate they can't see.
        setSelectedUserId('all');
        setShowAddStoreTypeModal(false);
        setNewStoreType({ name: '', description: '' });
        fetchStoreTypes(selectedCompany);
        alert(`A store type named "${newStoreType.name.trim()}" already exists for ${selectedCompany}. It may have been created under a different account or hidden by your user filter — switching to "All Users" so you can see it.`);
        return;
      }
      alert(msg);
    }
  };

  const handleDeleteStoreType = async (storeType, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${storeType.name}"?`)) return;
    try {
      await axiosInstance.delete(`/forecast/store-types/${storeType._id}`);
      fetchStoreTypes(selectedCompany);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleOpenEditModal = (storeType) => {
    setEditingStoreType({ ...storeType });
    setShowEditStoreTypeModal(true);
  };

  const handleSaveStoreType = async () => {
    if (!editingStoreType) return;
    try {
      await axiosInstance.put(`/forecast/store-types/${editingStoreType._id}`, {
        name: editingStoreType.name,
        description: editingStoreType.description
      });
      // Products are saved individually, so just close and refresh
      setShowEditStoreTypeModal(false);
      setEditingStoreType(null);
      fetchStoreTypes(selectedCompany);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  // Batch add from the picker: POST each selected SKU, keep the last
  // returned storeType (each response contains the full updated doc)
  const handleAddProductsFromPicker = async (sels) => {
    if (!editingStoreType) return;
    let latest = null;
    const errs = [];
    for (const sel of sels) {
      try {
        const res = await axiosInstance.post(`/forecast/store-types/${editingStoreType._id}/products`, {
          name: sel.name,
          quantity: 0,
          modelId: sel.modelId,
          bezel: sel.bezel,
          modelCode: sel.modelCode
        });
        latest = res.data.storeType;
      } catch (err) {
        errs.push(`${sel.name}: ${err.response?.data?.error || 'failed'}`);
      }
    }
    if (latest) setEditingStoreType(latest);
    setShowEditModelPicker(false);
    if (errs.length) alert(errs.join('\n'));
  };

  const handleUpdateProductQty = async (productId, quantity) => {
    if (!editingStoreType) return;
    try {
      const res = await axiosInstance.put(`/forecast/store-types/${editingStoreType._id}/products/${productId}`, { quantity });
      setEditingStoreType(res.data.storeType);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
    setEditingField(null);
  };

  const handleDeleteProduct = async (productId) => {
    if (!editingStoreType) return;
    try {
      const res = await axiosInstance.delete(`/forecast/store-types/${editingStoreType._id}/products/${productId}`);
      setEditingStoreType(res.data.storeType);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleUpdateDelivery = async (storeTypeId, month, quantity) => {
    try {
      await axiosInstance.put(`/forecast/store-types/${storeTypeId}/delivery`, { month, quantity });
      fetchStoreTypes(selectedCompany);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update delivery');
    }
    setEditingField(null);
  };

  // Drag handlers
  const handleDragStart = (e, storeType) => {
    setDraggedItem(storeType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, storeType) => {
    e.preventDefault();
    if (draggedItem && draggedItem._id !== storeType._id) {
      setDragOverItem(storeType._id);
    }
  };

  const handleDragLeave = () => setDragOverItem(null);

  const handleDrop = async (e, targetStoreType) => {
    e.preventDefault();
    if (!draggedItem || draggedItem._id === targetStoreType._id) return;

    const items = [...storeTypes];
    const draggedIdx = items.findIndex(i => i._id === draggedItem._id);
    const targetIdx = items.findIndex(i => i._id === targetStoreType._id);

    items.splice(draggedIdx, 1);
    items.splice(targetIdx, 0, draggedItem);

    for (let i = 0; i < items.length; i++) {
      if (items[i].displayOrder !== i) {
        await axiosInstance.put(`/forecast/store-types/${items[i]._id}/reorder`, { newOrder: i });
      }
    }

    setDraggedItem(null);
    setDragOverItem(null);
    fetchStoreTypes(selectedCompany);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };


  // Excel export
  const handleExport = async () => {
    try {
      const res = await axiosInstance.get('/forecast/store-types/export-excel', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.document' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `forecast-${new Date().getFullYear()}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert(err.response?.data?.error || 'Export failed');
    }
  };

  // Catalog manager (gear modal)
  const fetchCatalogModels = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/forecast/models', { params: { all: '1' } });
      setCatalogModels(res.data.models || []);
    } catch (err) {
      console.error('Error fetching full catalog:', err);
    }
  }, []);

  const handleOpenCatalogManager = () => {
    setShowProductManager(true);
    fetchCatalogModels();
  };

  const handleAddCustomModel = async () => {
    if (!newModel.name.trim() || !newModel.category.trim()) return;
    try {
      await axiosInstance.post('/forecast/models', newModel);
      setNewModel({ name: '', category: '', blackCode: '', whiteCode: '', redCode: '' });
      fetchCatalogModels();
      fetchModels(); // refresh picker source too
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add model');
    }
  };

  const handleToggleModelActive = async (model) => {
    try {
      await axiosInstance.patch(`/forecast/models/${model._id}/active`, { isActive: !model.isActive });
      fetchCatalogModels();
      fetchModels();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update model');
    }
  };

  // Summary modal
  const handleOpenSummary = async (type) => {
    setSummaryLoading(true);
    setShowSummary(type);
    try {
      const url = type === 'company' && selectedCompany
        ? `/forecast/store-types/summary?company=${encodeURIComponent(selectedCompany)}`
        : '/forecast/store-types/summary';
      const res = await axiosInstance.get(url);
      setSummaryData(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load summary');
      setShowSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

  // Sort products by size
  const sortProducts = (products) => {
    if (!products) return [];
    return [...products].sort((a, b) => {
      const sizeA = parseFloat(a.name.match(/^[\d.]+/)?.[0]) || 999;
      const sizeB = parseFloat(b.name.match(/^[\d.]+/)?.[0]) || 999;
      return sizeA - sizeB;
    });
  };

  if (error) {
    return <div className="srf-error">{error}</div>;
  }

  return (
    <ProtectedComponent functionCode="SA09" functionName="Sales Rollout Forecasting">
    <div className="srf-container">
      <div className="srf-header">
        <div className="srf-header-main">
          <h1>Sales Forecast & Rollout</h1>
          <p className="srf-subtitle">
            {activeTab === 'overview'
              ? 'Overview: Every customer — 16-week forecast + remaining months, one line per model'
              : activeTab === 'psi'
                ? 'DS PSI: Digital-signage demand vs incoming supply — projected on-hand (EOH) & stockouts, all-up by model'
              : activeTab === 'activity'
                ? 'Activity Log: Every forecast change — who did what, for which customer, and when'
                : activeTab === 'long'
                  ? 'Long-Term: Company → Store Type → Products + Monthly Deliveries (12 mo rolling)'
                  : 'Short-Term: Company → Models + Weekly Quantities (16 wk rolling)'}
          </p>
        </div>
        <div className="srf-header-contact">
          <span className="srf-header-contact-label">Questions, or need a product added to the catalog?</span>
          <span className="srf-header-contact-name">Contact Eric Olsen</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="srf-tabs">
        <button
          type="button"
          className={`srf-tab ${activeTab === 'overview' ? 'srf-tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          className={`srf-tab ${activeTab === 'long' ? 'srf-tab-active' : ''}`}
          onClick={() => setActiveTab('long')}
        >
          Long-Term (12 mo)
        </button>
        <button
          type="button"
          className={`srf-tab ${activeTab === 'short' ? 'srf-tab-active' : ''}`}
          onClick={() => setActiveTab('short')}
        >
          Short-Term (16 wk)
        </button>
        {PSI_TAB_ENABLED && (
          <button
            type="button"
            className={`srf-tab ${activeTab === 'psi' ? 'srf-tab-active' : ''}`}
            onClick={() => setActiveTab('psi')}
          >
            DS PSI
          </button>
        )}
        {isActivityAdmin && (
          <button
            type="button"
            className={`srf-tab ${activeTab === 'activity' ? 'srf-tab-active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity Log
          </button>
        )}
      </div>

      {/* Overview landing tab — all customers, no company selection needed */}
      {activeTab === 'overview' && <OverviewTab onOpenSnapshot={openSnapshot} />}

      {/* PSI tab — all-up by model, no company selection needed */}
      {PSI_TAB_ENABLED && activeTab === 'psi' && <PSIForecast onOpenSnapshot={openSnapshot} />}

      {/* User Filter + Company Selector (per-company tabs only) */}
      {activeTab !== 'overview' && activeTab !== 'activity' && activeTab !== 'psi' && (
      <div className="srf-selector-bar">
        {users.length > 0 && (
          <>
            <div className="srf-user-filter">
              <label>Filter by User:</label>
              <select value={selectedUserId || 'all'} onChange={(e) => setSelectedUserId(e.target.value)}>
                <option value="all">All Users</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="srf-selector-divider"></div>
          </>
        )}
        <label>Select Company:</label>
        <select
          value={selectedCompany || ''}
          onChange={(e) => setSelectedCompany(e.target.value || null)}
        >
          <option value="">-- Select Company --</option>
          {companies.map(c => (
            <option key={c._id} value={c.name}>{c.name}</option>
          ))}
        </select>
        <button className="srf-btn srf-btn-primary" onClick={() => setShowAddCompanyModal(true)}>
          + New Company
        </button>
        <button
          className="srf-btn srf-btn-icon"
          onClick={handleOpenCatalogManager}
          title="Manage SAI Model Catalog"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z"/>
          </svg>
        </button>
      </div>
      )}

      {/* Store Types — Long-Term tab only */}
      {activeTab === 'long' && selectedCompany && (
        <div className="srf-store-types-section">
          <div className="srf-section-header">
            <h2>Store Types for {selectedCompany}</h2>
            <div className="srf-section-header-right">
              <button className="srf-btn srf-btn-secondary" onClick={handleExport}>
                Export to Excel
              </button>
              <button className="srf-btn srf-btn-primary" onClick={() => setShowAddStoreTypeModal(true)}>
                + Add Store Type
              </button>
            </div>
          </div>

          <p className="srf-covered-legend">
            Greyed months are also covered by the 16-week Short-Term tab (still editable here).
          </p>

          {loading ? (
            <div className="srf-loading">Loading...</div>
          ) : filteredStoreTypes.length === 0 ? (
            <div className="srf-empty">
              {storeTypes.length === 0 ? (
                'No store types yet. Click "+ Add Store Type" to create one.'
              ) : (
                <>
                  {storeTypes.length} store type{storeTypes.length > 1 ? 's' : ''} for {selectedCompany} {storeTypes.length > 1 ? 'are' : 'is'} hidden by the current user filter.
                  {' '}
                  <button className="srf-show-all-btn" onClick={() => setSelectedUserId('all')}>
                    Show all
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="srf-store-types-list">
              {filteredStoreTypes.map(st => {
                return (
                  <div
                    key={st._id}
                    className={`srf-store-type-card ${dragOverItem === st._id ? 'drag-over' : ''} ${draggedItem?._id === st._id ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, st)}
                    onDragOver={(e) => handleDragOver(e, st)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, st)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Store Type Header - Click to edit */}
                    <div className="srf-store-type-header" onClick={() => handleOpenEditModal(st)}>
                      <div className="srf-drag-handle" title="Drag to reorder" onClick={e => e.stopPropagation()}>⋮⋮</div>
                      <div className="srf-store-type-info">
                        <span className="srf-store-type-name">{st.name}</span>
                        <span className="srf-store-type-desc">
                          {st.products?.length > 0
                            ? sortProducts(st.products).map(p => p.name).join(', ')
                            : '(no products - click here to add)'}
                        </span>
                      </div>
                      <div className="srf-store-type-stats">
                        {st.createdByName && (
                          <span className="srf-created-by">by {st.createdByName}</span>
                        )}
                      </div>
                      <div className="srf-store-type-actions" onClick={e => e.stopPropagation()}>
                        <button className="srf-btn srf-btn-sm srf-btn-secondary" onClick={() => handleOpenEditModal(st)}>
                          Edit
                        </button>
                        <button className="srf-btn srf-btn-sm srf-btn-danger" onClick={(e) => handleDeleteStoreType(st, e)}>
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Monthly Deliveries Grid */}
                    <div className="srf-monthly-grid">
                      {pastMonthsRevealed && (
                        <div className="srf-months-row srf-months-row-past">
                          {displayMonths.filter(m => m < currentMonth).map(month => {
                            const delivery = st.monthlyDeliveries?.find(m => m.month === month);
                            const qty = delivery?.quantity || 0;
                            const isEditing = editingField === `delivery-${st._id}-${month}`;
                            return (
                              <div key={month} className="srf-month-cell srf-month-past">
                                <div className="srf-month-label">{formatMonthShort(month)}</div>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    className="srf-month-input"
                                    value={editValue}
                                    autoFocus
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleUpdateDelivery(st._id, month, parseFloat(editValue) || 0)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateDelivery(st._id, month, parseFloat(editValue) || 0);
                                      if (e.key === 'Escape') setEditingField(null);
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={`srf-month-value ${qty > 0 ? 'has-value' : ''}`}
                                    onClick={() => { setEditingField(`delivery-${st._id}-${month}`); setEditValue(qty || ''); }}
                                  >
                                    {qty > 0 ? formatNumber(qty) : '-'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {/* Spacer mirrors current row's TOTAL cell so columns align vertically */}
                          <div className="srf-month-cell srf-month-total srf-month-spacer" aria-hidden="true" />
                        </div>
                      )}
                      <div className="srf-months-row">
                        {displayMonths.filter(m => m >= currentMonth).map(month => {
                          const delivery = st.monthlyDeliveries?.find(m => m.month === month);
                          const qty = delivery?.quantity || 0;
                          const isEditing = editingField === `delivery-${st._id}-${month}`;

                          return (
                            <div key={month} className={`srf-month-cell ${coveredMonths.includes(month) ? 'srf-month-covered' : ''}`}>
                              <div className="srf-month-label">{formatMonthShort(month)}</div>
                              {isEditing ? (
                                <input
                                  type="number"
                                  className="srf-month-input"
                                  value={editValue}
                                  autoFocus
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onBlur={() => handleUpdateDelivery(st._id, month, parseFloat(editValue) || 0)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateDelivery(st._id, month, parseFloat(editValue) || 0);
                                    if (e.key === 'Escape') setEditingField(null);
                                  }}
                                />
                              ) : (
                                <div
                                  className={`srf-month-value ${qty > 0 ? 'has-value' : ''}`}
                                  onClick={() => { setEditingField(`delivery-${st._id}-${month}`); setEditValue(qty || ''); }}
                                >
                                  {qty > 0 ? formatNumber(qty) : '-'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="srf-month-cell srf-month-total">
                          <div className="srf-month-label">Total</div>
                          <div className="srf-month-value has-value">
                            {formatNumber(st.monthlyDeliveries?.reduce((sum, m) => sum + (m.quantity || 0), 0) || 0)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="srf-prev-months-toggle"
                        onClick={() => setPastMonthsRevealed(v => !v)}
                        title={pastMonthsRevealed ? 'Hide past months' : 'Show past months'}
                      >
                        {pastMonthsRevealed ? '‹ hide past' : '‹ prev'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Short-Term Forecast — 16 week rolling weekly grid */}
      {activeTab === 'short' && selectedCompany && (
        <ShortTermForecast
          company={selectedCompany}
          selectedUserId={selectedUserId}
          models={models}
          onOpenSnapshot={openSnapshot}
        />
      )}

      {/* Activity Log — admin-only feed of every forecast change */}
      {activeTab === 'activity' && isActivityAdmin && <ActivityLog />}

      {/* Summary Modal */}
      {showSummary && (
        <div className="srf-modal-overlay" onClick={() => { setShowSummary(null); setSummaryData(null); }}>
          <div className="srf-modal srf-modal-xl" onClick={e => e.stopPropagation()}>
            {summaryLoading ? (
              <div className="srf-loading">Loading summary...</div>
            ) : summaryData ? (
              <div className="srf-summary">
                <h2 className="srf-summary-title">
                  {showSummary === 'company' && selectedCompany
                    ? `${selectedCompany} Forecast Summary`
                    : 'Total Forecast Summary — All Companies'}
                </h2>

                {/* KPI Cards */}
                <div className="srf-kpi-row">
                  {(showSummary === 'all' ? summaryData.companies : [selectedCompany]).map(comp => {
                    const s = summaryData.companySummaries[comp];
                    if (!s) return null;
                    return (
                      <div key={comp} className="srf-kpi-card">
                        <div className="srf-kpi-label">{comp}</div>
                        <div className="srf-kpi-value">{s.totalStores.toLocaleString()}</div>
                        <div className="srf-kpi-sub">stores</div>
                        <div className="srf-kpi-value srf-kpi-highlight">{s.totalLabels.toLocaleString()}</div>
                        <div className="srf-kpi-sub">total labels</div>
                      </div>
                    );
                  })}
                  {showSummary === 'all' && (
                    <div className="srf-kpi-card srf-kpi-grand">
                      <div className="srf-kpi-label">ALL</div>
                      <div className="srf-kpi-value">{summaryData.grandTotalStores.toLocaleString()}</div>
                      <div className="srf-kpi-sub">stores</div>
                      <div className="srf-kpi-value srf-kpi-highlight">{summaryData.grandTotalLabels.toLocaleString()}</div>
                      <div className="srf-kpi-sub">total labels</div>
                    </div>
                  )}
                </div>

                {/* Monthly Labels Chart */}
                <div className="srf-chart-section">
                  <h3>Monthly Label Output</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={showSummary === 'all' ? summaryData.grandMonthly : summaryData.companySummaries[selectedCompany]?.monthlyData || []}>
                      <defs>
                        <linearGradient id="labelGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }}
                        formatter={(value) => [value.toLocaleString(), 'Labels']}
                      />
                      <Area type="monotone" dataKey="labels" stroke="#3b82f6" strokeWidth={2} fill="url(#labelGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Monthly Stores Chart */}
                <div className="srf-chart-section">
                  <h3>Monthly Store Deployments</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={showSummary === 'all' ? summaryData.grandMonthly : summaryData.companySummaries[selectedCompany]?.monthlyData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }}
                        formatter={(value) => [value.toLocaleString(), 'Stores']}
                      />
                      <Bar dataKey="stores" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Product Breakdown Pie */}
                <div className="srf-chart-section">
                  <h3>Product Mix</h3>
                  <div className="srf-chart-row">
                    {(showSummary === 'all' ? summaryData.companies : [selectedCompany]).map(comp => {
                      const s = summaryData.companySummaries[comp];
                      if (!s || !s.topProducts.length) return null;
                      return (
                        <div key={comp} className="srf-chart-pie-wrapper">
                          <h4>{comp}</h4>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={s.topProducts}
                                dataKey="total"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                                fontSize={11}
                              >
                                {s.topProducts.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0' }}
                                formatter={(value) => value.toLocaleString()}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Store Type Breakdown Table */}
                <div className="srf-chart-section">
                  <h3>Store Type Breakdown</h3>
                  {(showSummary === 'all' ? summaryData.companies : [selectedCompany]).map(comp => {
                    const s = summaryData.companySummaries[comp];
                    if (!s) return null;
                    return (
                      <div key={comp} className="srf-summary-table-wrapper">
                        {showSummary === 'all' && <h4>{comp}</h4>}
                        <table className="srf-summary-table">
                          <thead>
                            <tr>
                              <th>Store Type</th>
                              <th>Stores</th>
                              <th>Labels/Store</th>
                              <th>Owner</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s.storeTypes.map((st, i) => (
                              <tr key={i}>
                                <td>{st.name}</td>
                                <td>{st.stores.toLocaleString()}</td>
                                <td>{st.labelsPerStore.toLocaleString()}</td>
                                <td>{st.createdByName || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>

                <div className="srf-modal-actions">
                  <button className="srf-btn srf-btn-secondary" onClick={() => { setShowSummary(null); setSummaryData(null); }}>Close</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* SAI Catalog Manager Modal */}
      {showProductManager && (
        <div className="srf-modal-overlay" onClick={() => setShowProductManager(false)}>
          <div className="srf-modal srf-modal-lg" onClick={e => e.stopPropagation()}>
            <h3>⚙ SAI Model Catalog</h3>
            <div className="srf-catalog-scroll">
              {[...new Set(catalogModels.map(m => m.category))].map(cat => (
                <div key={cat}>
                  <div className="srf-catalog-cat">{cat}</div>
                  {catalogModels.filter(m => m.category === cat).map(m => (
                    <div key={m._id} className={`srf-catalog-row ${m.isActive ? '' : 'srf-catalog-inactive'}`}>
                      <span className="srf-catalog-name">{m.name}</span>
                      <span className="srf-catalog-codes">
                        {m.blackCode && <span className="srf-product-code-chip">{m.isAccessory ? '' : 'B · '}{m.blackCode}</span>}
                        {m.whiteCode && <span className="srf-product-code-chip">W · {m.whiteCode}</span>}
                        {m.redCode && <span className="srf-product-code-chip">R · {m.redCode}</span>}
                      </span>
                      <button
                        type="button"
                        className={`srf-btn srf-btn-sm ${m.isActive ? 'srf-btn-danger' : 'srf-btn-primary'}`}
                        onClick={() => handleToggleModelActive(m)}
                      >
                        {m.isActive ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <h4>Add Custom Model</h4>
            <div className="srf-catalog-add">
              <input type="text" placeholder='Name, e.g. 2.9" Newton PRO BWRY'
                value={newModel.name}
                onChange={e => setNewModel({ ...newModel, name: e.target.value })} />
              <input type="text" placeholder="Category" list="srf-cat-list"
                value={newModel.category}
                onChange={e => setNewModel({ ...newModel, category: e.target.value })} />
              <datalist id="srf-cat-list">
                {[...new Set(catalogModels.map(m => m.category))].map(c => <option key={c} value={c} />)}
              </datalist>
              <input type="text" placeholder="Black code"
                value={newModel.blackCode}
                onChange={e => setNewModel({ ...newModel, blackCode: e.target.value })} />
              <input type="text" placeholder="White code"
                value={newModel.whiteCode}
                onChange={e => setNewModel({ ...newModel, whiteCode: e.target.value })} />
              <input type="text" placeholder="Red code"
                value={newModel.redCode}
                onChange={e => setNewModel({ ...newModel, redCode: e.target.value })} />
              <button className="srf-btn srf-btn-primary" onClick={handleAddCustomModel}
                disabled={!newModel.name.trim() || !newModel.category.trim() || (!newModel.blackCode.trim() && !newModel.whiteCode.trim() && !newModel.redCode.trim())}>
                Add
              </button>
            </div>
            <div className="srf-modal-actions" style={{ justifyContent: 'space-between' }}>
              <button className="srf-btn srf-btn-danger" onClick={handleDeleteCompany} disabled={!selectedCompany}>
                {selectedCompany ? `Delete "${selectedCompany}"` : 'Select a company first'}
              </button>
              <button className="srf-btn srf-btn-secondary" onClick={() => setShowProductManager(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="srf-modal-overlay" onClick={() => setShowAddCompanyModal(false)}>
          <div className="srf-modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Company</h3>
            <div className="srf-form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="e.g., WFC, Kroger, Target"
                autoFocus
              />
            </div>
            <div className="srf-modal-actions">
              <button className="srf-btn srf-btn-secondary" onClick={() => setShowAddCompanyModal(false)}>Cancel</button>
              <button className="srf-btn srf-btn-primary" onClick={handleAddCompany}>Create Company</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Store Type Modal */}
      {showAddStoreTypeModal && (
        <div className="srf-modal-overlay" onClick={() => setShowAddStoreTypeModal(false)}>
          <div className="srf-modal" onClick={e => e.stopPropagation()}>
            <h3>Add Store Type for {selectedCompany}</h3>
            <div className="srf-form-group">
              <label>Store Type Name</label>
              <input
                type="text"
                value={newStoreType.name}
                onChange={(e) => setNewStoreType({ ...newStoreType, name: e.target.value })}
                placeholder="e.g., Small Store, Large Store"
                autoFocus
              />
            </div>
            <div className="srf-form-group">
              <label>Notes (optional)</label>
              <input
                type="text"
                value={newStoreType.description}
                onChange={(e) => setNewStoreType({ ...newStoreType, description: e.target.value })}
                placeholder="Internal notes"
              />
            </div>
            <div className="srf-modal-actions">
              <button className="srf-btn srf-btn-secondary" onClick={() => setShowAddStoreTypeModal(false)}>Cancel</button>
              <button className="srf-btn srf-btn-primary" onClick={handleAddStoreType}>Create Store Type</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Store Type Modal */}
      {showEditStoreTypeModal && editingStoreType && (
        <div className="srf-modal-overlay" onClick={() => { setShowEditStoreTypeModal(false); setEditingStoreType(null); setShowEditModelPicker(false); }}>
          <div className="srf-modal srf-modal-lg" onClick={e => e.stopPropagation()}>
            <h3>Edit Store Type: {editingStoreType.name}</h3>

            <div className="srf-form-row">
              <div className="srf-form-group">
                <label>Store Type Name</label>
                <input
                  type="text"
                  value={editingStoreType.name}
                  onChange={(e) => setEditingStoreType({ ...editingStoreType, name: e.target.value })}
                />
              </div>
              <div className="srf-form-group">
                <label>Notes</label>
                <input
                  type="text"
                  value={editingStoreType.description || ''}
                  onChange={(e) => setEditingStoreType({ ...editingStoreType, description: e.target.value })}
                />
              </div>
            </div>

            <h4>Products</h4>
            <div className="srf-edit-products-list">
              <div className="srf-edit-product-header">
                <span>Product</span>
                <span>Quantity</span>
                <span></span>
              </div>
              {sortProducts(editingStoreType.products).map(product => {
                const isEditing = editingField === `edit-product-${product._id}`;
                return (
                  <div key={product._id} className="srf-edit-product-row">
                    <span className="srf-edit-product-name">
                      {product.name}
                      {product.modelCode && (
                        <span className="srf-product-code-chip">
                          {product.bezel ? `${product.bezel === 'white' ? 'W' : product.bezel === 'red' ? 'R' : 'B'} · ` : ''}{product.modelCode}
                        </span>
                      )}
                    </span>
                    <span className="srf-edit-product-qty">
                      {isEditing ? (
                        <input
                          type="number"
                          className="srf-inline-input"
                          value={editValue}
                          autoFocus
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleUpdateProductQty(product._id, parseFloat(editValue) || 0)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateProductQty(product._id, parseFloat(editValue) || 0);
                            if (e.key === 'Escape') setEditingField(null);
                          }}
                        />
                      ) : (
                        <span
                          className="srf-editable-value"
                          onClick={() => { setEditingField(`edit-product-${product._id}`); setEditValue(product.quantity || ''); }}
                        >
                          {formatNumber(product.quantity)}
                        </span>
                      )}
                    </span>
                    <button
                      className="srf-btn-icon srf-btn-danger-icon"
                      onClick={() => handleDeleteProduct(product._id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              {(!editingStoreType.products || editingStoreType.products.length === 0) && (
                <div className="srf-no-products">No products yet. Add one below.</div>
              )}
            </div>

            {showEditModelPicker ? (
              <ModelPicker
                models={models}
                existingKeys={(editingStoreType.products || []).map(p => `${p.name}|${p.bezel}`)}
                onAddMany={handleAddProductsFromPicker}
                onClose={() => setShowEditModelPicker(false)}
              />
            ) : (
              <button
                type="button"
                className="srf-btn srf-btn-primary"
                onClick={() => setShowEditModelPicker(true)}
              >
                + Add Model
              </button>
            )}

            <div className="srf-modal-actions">
              <button className="srf-btn srf-btn-secondary" onClick={() => { setShowEditStoreTypeModal(false); setEditingStoreType(null); setShowEditModelPicker(false); fetchStoreTypes(selectedCompany); }}>
                Close
              </button>
              <button className="srf-btn srf-btn-primary" onClick={handleSaveStoreType}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {snapshotWeek && (
        <WeeklySnapshotModal
          weekKey={snapshotWeek.key}
          weekLabel={snapshotWeek.label}
          onClose={() => setSnapshotWeek(null)}
        />
      )}
    </div>
    </ProtectedComponent>
  );
}

export default SalesRolloutForecasting;
